/**
 * E12 · the vicious-cycle EDIT SCRIPT — shared by the mobile and portal editors.
 *
 * Under per-edit event sourcing (founder G1 2026-08-29, D-per-edit) an editor no longer saves a
 * graph; it saves WHAT THE USER DID: rename, add a node, remove a node, toggle an edge, relabel a
 * node. Both editors record those as `CycleEditOp`s while the user works and flush them on Save as
 * one command each, in order — so the log holds the exact history, not a diff guessed afterwards
 * (which cannot tell "removed node 2" from "renamed node 2 and removed node 3").
 *
 * `planCycleCommands` turns the recorded ops into the commands to send:
 *   • consecutive keystrokes into the SAME field collapse into one command (the last value) —
 *     typing is not history;
 *   • a template pick on an EXISTING cycle expands into the edits it really is: remove every node,
 *     add each template node, draw each template edge (and rename), because the graph was replaced;
 *   • commands that would change nothing against the running model are dropped, so a name typed
 *     and typed back leaves no fact behind.
 * The first save of a NEW cycle does not use this: it is one CreateViciousCycle carrying the whole
 * draft (a template clone or blank), exactly as the board models it.
 *
 * Pure. No I/O, no clock.
 */

export type CycleGraphNode = { thought: string; edges: boolean[] };
export type CycleGraphModel = { name: string; thoughts: CycleGraphNode[] };

export type CycleEditOp =
  | { kind: 'rename'; name: string }
  | { kind: 'add'; thought: string }
  | { kind: 'remove'; index: number }
  | { kind: 'toggle'; from: number; to: number; active: boolean }
  | { kind: 'edit'; index: number; thought: string }
  | {
      kind: 'template';
      templateId: string;
      name: string;
      thoughts: CycleGraphNode[];
    };

/** What actually goes over the wire — a template pick has been expanded. */
export type CycleCommand = Exclude<CycleEditOp, { kind: 'template' }>;

const square = (thoughts: ReadonlyArray<CycleGraphNode>): CycleGraphNode[] => {
  const n = thoughts.length;
  return thoughts.map((t) => ({
    thought: t.thought ?? '',
    edges: Array.from({ length: n }, (_, j) => Boolean(t.edges?.[j])),
  }));
};

/** Apply one command to a model — the same arithmetic the server's fold applies. */
export function applyCycleCommand(
  model: CycleGraphModel,
  c: CycleCommand,
): CycleGraphModel {
  const thoughts = square(model.thoughts);
  switch (c.kind) {
    case 'rename':
      return { ...model, name: c.name };
    case 'add':
      return {
        ...model,
        thoughts: [
          ...thoughts.map((t) => ({ ...t, edges: [...t.edges, false] })),
          {
            thought: c.thought,
            edges: Array.from({ length: thoughts.length + 1 }, () => false),
          },
        ],
      };
    case 'remove':
      if (c.index < 0 || c.index >= thoughts.length)
        return { ...model, thoughts };
      return {
        ...model,
        thoughts: thoughts
          .filter((_, i) => i !== c.index)
          .map((t) => ({
            ...t,
            edges: t.edges.filter((_, j) => j !== c.index),
          })),
      };
    case 'toggle':
      return {
        ...model,
        thoughts: thoughts.map((t, i) =>
          i === c.from
            ? {
                ...t,
                edges: t.edges.map((e, j) => (j === c.to ? c.active : e)),
              }
            : t,
        ),
      };
    case 'edit':
      return {
        ...model,
        thoughts: thoughts.map((t, i) =>
          i === c.index ? { ...t, thought: c.thought } : t,
        ),
      };
  }
}

/** Is this command a no-op against the model? (The server would also record nothing.) */
function changesNothing(model: CycleGraphModel, c: CycleCommand): boolean {
  const thoughts = square(model.thoughts);
  switch (c.kind) {
    case 'rename':
      return (model.name ?? '') === (c.name ?? '');
    // an edit or toggle addressed BEYOND the running model cannot be a real edit — the user never saw
    // such a node — so it is a phantom (a list widget echoing a programmatic mutation) and is dropped
    // rather than sent to be rejected, which would abort the whole flush
    case 'edit':
      return (
        c.index >= thoughts.length || thoughts[c.index].thought === c.thought
      );
    case 'toggle':
      return (
        c.from >= thoughts.length ||
        c.to >= thoughts.length ||
        thoughts[c.from].edges[c.to] === c.active
      );
    case 'remove':
      return c.index < 0 || c.index >= thoughts.length;
    case 'add':
      return false;
  }
}

/** The edits a template pick really is, against the current graph. */
function expandTemplate(
  model: CycleGraphModel,
  op: Extract<CycleEditOp, { kind: 'template' }>,
): CycleCommand[] {
  const out: CycleCommand[] = [];
  if ((model.name ?? '') !== (op.name ?? '')) {
    out.push({ kind: 'rename', name: op.name ?? '' });
  }
  for (let i = model.thoughts.length - 1; i >= 0; i -= 1) {
    out.push({ kind: 'remove', index: i });
  }
  const tpl = square(op.thoughts);
  tpl.forEach((t) => out.push({ kind: 'add', thought: t.thought }));
  tpl.forEach((t, from) =>
    t.edges.forEach((active, to) => {
      if (active) out.push({ kind: 'toggle', from, to, active: true });
    }),
  );
  return out;
}

/** Two consecutive commands that are the same field being typed into — keep the last. */
function sameField(a: CycleCommand, b: CycleCommand): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'rename') return true;
  if (a.kind === 'edit' && b.kind === 'edit') return a.index === b.index;
  if (a.kind === 'toggle' && b.kind === 'toggle')
    return a.from === b.from && a.to === b.to;
  return false;
}

/**
 * The commands to send for a Save, oldest first, against the graph as it was LOADED (the state the
 * server's fold holds). Idempotent: planning the same ops twice yields the same commands.
 */
export function planCycleCommands(
  base: CycleGraphModel,
  ops: ReadonlyArray<CycleEditOp>,
): CycleCommand[] {
  // 1 — expand template picks against the running model, and coalesce typing
  let model: CycleGraphModel = {
    name: base.name ?? '',
    thoughts: square(base.thoughts),
  };
  const raw: CycleCommand[] = [];
  for (const op of ops) {
    const cs = op.kind === 'template' ? expandTemplate(model, op) : [op];
    for (const c of cs) {
      const prev = raw[raw.length - 1];
      if (prev && sameField(prev, c)) {
        raw[raw.length - 1] = c; // typing: the last value is the edit
      } else {
        raw.push(c);
      }
    }
    // the model tracks every op as recorded, so later indexes mean what the user saw
    for (const c of cs) model = applyCycleCommand(model, c);
  }
  // 2 — drop commands that change nothing against the model as it stands when they run
  const out: CycleCommand[] = [];
  let m: CycleGraphModel = {
    name: base.name ?? '',
    thoughts: square(base.thoughts),
  };
  for (const c of raw) {
    if (!changesNothing(m, c)) out.push(c);
    m = applyCycleCommand(m, c);
  }
  return out;
}
